// Lightweight ICS parser focused on DTSTART/DTEND/SUMMARY and RRULE weekly BYDAY

const DAY_TO_NUM = { MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6, SU: 7 };

function unfoldLines(text) {
  return text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .reduce((acc, line) => {
      if (/^[ \t]/.test(line) && acc.length) {
        acc[acc.length - 1] += line.slice(1);
      } else if (line.trim().length) {
        acc.push(line);
      }
      return acc;
    }, []);
}

function parseProps(lines) {
  const props = {};
  for (const ln of lines) {
    const idx = ln.indexOf(':');
    if (idx === -1) continue;
    const keyPart = ln.slice(0, idx);
    const value = ln.slice(idx + 1);
    const key = keyPart.split(';')[0].toUpperCase();
    props[key] = value;
  }
  return props;
}

function parseICSDateTime(dt) {
  // Accept forms like 20240501T170000Z or 20240501T170000
  if (!dt) return null;
  const iso = dt.replace(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z)?$/, (_m, y, mo, d, h, mi, s, z) => {
    const base = `${y}-${mo}-${d}T${h}:${mi}:${s}`;
    return z ? `${base}Z` : base;
  });
  const date = new Date(iso);
  if (isNaN(date.getTime())) return null;
  return date;
}

function toDateStr(date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

function toTimeStr(date) {
  const iso = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
  return iso.slice(11, 16);
}

function parseRRule(rrule) {
  // expects like FREQ=WEEKLY;BYDAY=MO,WE;UNTIL=20240630T235959Z
  const out = {};
  for (const pair of rrule.split(';')) {
    const [k, v] = pair.split('=');
    if (!k) continue;
    out[k.toUpperCase()] = v;
  }
  return out;
}

export function parseICS(text) {
  const lines = unfoldLines(text);
  const events = [];
  let buf = [];
  let inEvent = false;
  for (const ln of lines) {
    if (ln === 'BEGIN:VEVENT') {
      buf = [];
      inEvent = true;
      continue;
    }
    if (ln === 'END:VEVENT') {
      const props = parseProps(buf);
      events.push(props);
      buf = [];
      inEvent = false;
      continue;
    }
    if (inEvent) buf.push(ln);
  }

  /** @type {{eventos: Array<any>, cursadas: Array<any>, ignored: number, errors: string[]}} */
  const result = { eventos: [], cursadas: [], ignored: 0, errors: [] };

  for (const ev of events) {
    const summary = (ev.SUMMARY || '').trim();
    const dtStart = parseICSDateTime(ev.DTSTART);
    const dtEnd = parseICSDateTime(ev.DTEND) || (dtStart ? new Date(dtStart.getTime() + 60 * 60 * 1000) : null);
    if (!dtStart) { result.ignored++; continue; }

    if (ev.RRULE) {
      const rule = parseRRule(ev.RRULE);
      if ((rule.FREQ || '').toUpperCase() !== 'WEEKLY' || !rule.BYDAY) {
        result.ignored++;
        continue;
      }

      const days = String(rule.BYDAY)
        .split(',')
        .map((x) => x.trim().toUpperCase())
        .map((d) => DAY_TO_NUM[d])
        .filter(Boolean);
      const untilDate = rule.UNTIL ? parseICSDateTime(rule.UNTIL) : (dtEnd || dtStart);
      if (!days.length || !untilDate) { result.ignored++; continue; }

      // Try to extract subject name if SUMMARY starts with "Cursada:" like export
      let materia = summary;
      const m = /^\s*Cursada:\s*(.*)$/i.exec(summary);
      if (m) materia = m[1].trim();

      result.cursadas.push({
        id: crypto.randomUUID?.() || String(Date.now() + Math.random()),
        materia: materia || 'Cursada',
        dias: days, // numbers 1..7; downstream will accept 1..6
        horaInicio: toTimeStr(dtStart),
        horaFin: dtEnd ? toTimeStr(dtEnd) : toTimeStr(new Date(dtStart.getTime() + 60 * 60 * 1000)),
        desde: toDateStr(dtStart),
        hasta: toDateStr(untilDate),
      });
    } else {
      // single event
      let tipo = 'Otro';
      let titulo = summary || 'Evento';
      // Try to split like "Parcial: Derecho Civil I"
      const idx = summary.indexOf(':');
      if (idx > 0) {
        const left = summary.slice(0, idx).trim();
        const right = summary.slice(idx + 1).trim();
        if (['Parcial', 'Final', 'TP', 'Exposición', 'Otro'].includes(left)) {
          tipo = left;
          titulo = right || left;
        }
      }
      result.eventos.push({
        id: crypto.randomUUID?.() || String(Date.now() + Math.random()),
        tipo,
        titulo,
        fecha: toDateStr(dtStart),
        horaInicio: toTimeStr(dtStart),
        horaFin: dtEnd ? toTimeStr(dtEnd) : toTimeStr(new Date(dtStart.getTime() + 60 * 60 * 1000)),
      });
    }
  }

  return result;
}


